import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import PageMeta from '@/components/PageMeta';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const HELP_TOPICS = [
  { t: 'Getting started', b: 'Once Valanti Labs is publicly available, getting started will involve creating an account, choosing a supported movement, and reviewing the recording guidance built into the app.' },
  { t: 'Account access', b: 'Account creation and sign-in will be available from within the application. We will document common access issues here as the early-access programme progresses.' },
  { t: 'Recording guidance', b: 'The app provides on-screen guidance for camera position, distance, framing, and lighting. Following these instructions improves the quality and reliability of the feedback you receive.' },
  { t: 'Camera positioning', b: 'The phone should be positioned so the relevant part of the body is clearly visible for the chosen movement. The app provides specific guidance per supported movement.' },
  { t: 'Supported movements', b: 'Valanti Labs analyses a specific list of supported movements. Recording an unsupported movement may result in a low-reliability response rather than feedback.' },
  { t: 'Upload problems', b: 'If a video fails to upload, check your network connection and storage. We will publish detailed troubleshooting steps as the application becomes available.' },
  { t: 'Video quality problems', b: 'Poor lighting, occluded body parts, unusual angles, or shaky footage may reduce reliability. The app will tell you when the available evidence is not strong enough.' },
  { t: 'Understanding feedback', b: 'Feedback is presented as structured plain-language observations, alongside an honest indication of reliability for each supported dimension.' },
  { t: 'Privacy questions', b: 'See our Privacy page for the current draft of our privacy approach. You may also use the contact form for specific privacy questions.' },
  { t: 'Requesting account deletion', b: 'See the Account Deletion page for guidance on how to begin a deletion request once the application is available.' },
  { t: 'Reporting a technical problem', b: 'Please use the contact form with the topic "Support" and include as much detail as possible about the issue and the device you are using.' },
  { t: 'Reporting a safety concern', b: 'If you are experiencing concerning physical symptoms, please stop and seek qualified professional care. To report an in-app safety concern, please use the contact form with the topic "Support".' },
  { t: 'Contacting support', b: 'Use the contact form on this site. A monitored support email address will be published once it is active.' },
];

export default function Support() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => { api.get('/faqs').then((r) => setFaqs(r.data || [])); }, []);
  return (
    <div>
      <PageMeta
        title="Support"
        description="The Valanti Labs support centre — help topics, frequently asked questions, account deletion and contact information."
      />
      <section className="vl-container py-16 lg:py-24 max-w-3xl">
        <p className="mono-label text-vl-violet">Support</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-vl-ink">
          Support centre.
        </h1>
        <p className="mt-6 text-lg text-vl-ink/70 leading-relaxed">
          Help topics, frequently asked questions, account deletion guidance and how to get in touch with the Valanti
          Labs team. The application is currently in pre-release preparation.
        </p>
      </section>

      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-20 lg:py-28">
          <h2 className="font-serif-display text-3xl text-vl-ink mb-10">Help topics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {HELP_TOPICS.map((t) => (
              <details key={t.t} className="group rounded-2xl bg-vl-surface border hairline p-5 open:shadow-md">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-serif-display text-lg text-vl-ink">
                  <span>{t.t}</span>
                  <span className="text-vl-violet text-xl transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm text-vl-ink/70 leading-relaxed">{t.b}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="vl-container py-24 lg:py-32 max-w-3xl">
        <h2 className="font-serif-display text-3xl text-vl-ink">Frequently asked questions</h2>
        <p className="mt-3 text-vl-ink/70">Common questions about Valanti Labs and the application.</p>

        <Accordion type="single" collapsible className="mt-10" data-testid="support-faqs">
          {faqs.map((f) => (
            <AccordionItem key={f.id} value={f.id} className="border-b hairline">
              <AccordionTrigger className="text-left font-serif-display text-lg text-vl-ink hover:no-underline py-5">
                {f.question}
              </AccordionTrigger>
              <AccordionContent className="text-vl-ink/75 leading-relaxed pb-6">
                {f.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link to="/contact" className="rounded-3xl bg-vl-surface border hairline p-7 hover:shadow-md transition-shadow">
            <h3 className="font-serif-display text-xl text-vl-ink">Contact us</h3>
            <p className="text-sm text-vl-ink/70 mt-2">Use the contact form for support, privacy, partnership and other enquiries.</p>
          </Link>
          <Link to="/support/delete-account" className="rounded-3xl bg-vl-surface border hairline p-7 hover:shadow-md transition-shadow">
            <h3 className="font-serif-display text-xl text-vl-ink">Account deletion</h3>
            <p className="text-sm text-vl-ink/70 mt-2">How to begin a deletion request once your account exists.</p>
          </Link>
        </div>

        <p className="mt-12 text-xs text-vl-ink/55">
          Until our monitored support mailboxes are confirmed active, please use the contact form on this site —
          messages reach a member of the Valanti Labs team.
        </p>
      </section>
    </div>
  );
}
